import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { buildAclGroups, getCorrectEnums } from '../../../utils/Functions';
import { AdminAclRequest, AdminService } from '../AdminService';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { ADMIN_ACL_GROUPS } from './AdminAclGroups';

export interface IAdminAccessRightsModalProps {
    show: boolean;
    closeModal: () => void;
    id: number;
    aclArr: string[];
    email: string;
}

export function AdminAccessRightsModal({ show, closeModal, id, aclArr, email }: IAdminAccessRightsModalProps) {
    const { AdminAclEnum } = useSelector((state: any) => state.enums);
    const { user } = useSelector((state: any) => state.auth);

    const [accessRight, setAccessRight] = useState<AdminAclRequest>({ adminAcl: aclArr });
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (show) {
            setAccessRight({ adminAcl: aclArr });
            setFilter('');
        }
    }, [show, aclArr]);

    const groups = useMemo(() => buildAclGroups(ADMIN_ACL_GROUPS, AdminAclEnum ?? []), [AdminAclEnum]);

    const selectedCount = accessRight.adminAcl?.length ?? 0;
    const totalCount = AdminAclEnum?.length ?? 0;
    const filterQuery = filter.toLowerCase().trim();

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setAccessRight(prev => ({
            ...prev,
            adminAcl: checked
                ? [...(prev.adminAcl ?? []), value]
                : (prev.adminAcl ?? []).filter(item => item !== value),
        }));
    };

    const setGroupSelection = (items: string[], select: boolean) => {
        setAccessRight(prev => {
            const current = new Set(prev.adminAcl ?? []);
            items.forEach(i => (select ? current.add(i) : current.delete(i)));
            return { ...prev, adminAcl: Array.from(current) };
        });
    };

    const clearAll = () => setAccessRight(prev => ({ ...prev, adminAcl: [] }));

    const saveAccessRight = () => {
        AdminService.editAdminAcl(id, accessRight)
            .then(response => {
                SuccessToast('Successfully updated access right');
                if (user.id === response.id) {
                    localStorage.setItem('adminAcl', String(response?.adminAcl));
                    window.location.reload();
                }
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <Modal show={show} onHide={closeModal} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Update Access Rights for {email}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center gap-2 mb-4">
                    <InputGroup style={{ maxWidth: 320 }}>
                        <Form.Control
                            placeholder="Filter permissions"
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        />
                    </InputGroup>
                    <small className="text-muted ms-auto">
                        {selectedCount} of {totalCount} selected
                    </small>
                    <Button size="sm" variant="outline-primary" onClick={clearAll} disabled={!selectedCount}>
                        Clear all
                    </Button>
                </div>

                {groups.map(group => {
                    const visibleItems = filterQuery
                        ? group.items.filter(i => getCorrectEnums(i).toLowerCase().includes(filterQuery))
                        : group.items;

                    if (!visibleItems.length) return null;

                    const selectedInGroup = group.items.filter(i => accessRight.adminAcl?.includes(i)).length;

                    return (
                        <section key={group.key} className="mb-4">
                            <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="mb-0">{group.label}</h6>
                                    <Badge bg="light" text="dark" className="fw-normal">
                                        {selectedInGroup}/{group.items.length}
                                    </Badge>
                                </div>
                                <div className="d-flex gap-2">
                                    {selectedInGroup < group.items.length && (
                                        <Button
                                            size="sm"
                                            variant="link"
                                            className="p-0 text-decoration-none"
                                            onClick={() => setGroupSelection(group.items, true)}
                                        >
                                            Select all
                                        </Button>
                                    )}
                                    {selectedInGroup > 0 && selectedInGroup < group.items.length && (
                                        <span className="text-muted">·</span>
                                    )}
                                    {selectedInGroup > 0 && (
                                        <Button
                                            size="sm"
                                            variant="link"
                                            className="p-0 text-decoration-none"
                                            onClick={() => setGroupSelection(group.items, false)}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="row g-2">
                                {visibleItems.map(item => (
                                    <Form.Group key={item} className="col-4">
                                        <Form.Check
                                            label={getCorrectEnums(item)}
                                            value={item}
                                            checked={accessRight.adminAcl?.includes(item) ?? false}
                                            onChange={handleToggle}
                                        />
                                    </Form.Group>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={closeModal}>
                    Close
                </Button>
                <Button onClick={saveAccessRight}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
}
